import React, { useState, useEffect, useCallback, useRef } from 'react';
import { KeyRound, Plus, Pencil, Trash2, Save, X, RefreshCw, ChevronLeft, ChevronRight, Search, GripVertical } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { credentialHealthApi, adminManagementApi } from '../../services/api';
import LookupSelect from '../../components/common/LookupSelect';
import StuiSelect from '../../components/common/StuiSelect';
import StuiDateTime from '../../components/common/StuiDateTime';
import { dayKey, formatTimestampDate, localDayToUtcRange } from '../../utils/dateTime';

/**
 * Administration > System > Credential Health
 *
 * CRUD UI for AppSec_Secrets — the inventory of every credential the stack depends on
 * and when each one next needs a human.
 *
 * Two kinds of row, because the stack contains both. Deadline rows die on a date
 * (Graph client secret, TLS certificates). Rotation rows never expire and stay valid
 * until someone changes them (SQL passwords, storage account keys, non-expiring PATs),
 * so the risk is age rather than a deadline. The API returns one derived DaysRemaining
 * for either kind, which is why one page covers both — this page does no date maths.
 *
 * NEVER displays a credential value. There is no value to display: AppSec_Secrets has
 * no such column, by design. Preferences > Local Dev holds the same line.
 *
 * SysAdmin only (route-guarded in App.js), no permission key — same argument as
 * Scheduled Jobs and Audit Trail - Admin.
 *
 * STUI-only: every class used here already exists in STUI.css. No new classes, no
 * inline styles, no edits to STUI.
 */

const STATUS_COLOURS = {
  Expired:  'stui-badge-red',
  Critical: 'stui-badge-orange',
  Warning:  'stui-badge-amber',
  OK:       'stui-badge-green',
  Unknown:  'stui-badge-gray'
};

const MODE_COLOURS = {
  Deadline: 'stui-badge-blue',
  Rotation: 'stui-badge-teal'
};

const getStatusClass = (status) => STATUS_COLOURS[status] || 'stui-badge-gray';
const getModeClass   = (mode)   => MODE_COLOURS[mode]     || 'stui-badge-gray';

// These columns are UTC instants entered with a date picker: the picker holds the person's local
// day, and the stored instant is that day's local midnight so it reads back as the same day.
const toDateInput = (value) => dayKey(value);
const fmtDate = (value) => formatTimestampDate(value) || '—';
const dayToInstant = (day) => (day ? localDayToUtcRange(day)?.from ?? null : null);
// A day the person did not change keeps its stored instant (often a Key Vault time of day), so
// saving an unrelated field never truncates it to midnight.
const keepOrPick = (day, stored) => (stored && day && dayKey(stored) === day ? stored : dayToInstant(day));

const fmtDays = (days) => {
  if (days === null || days === undefined) return 'Unknown';
  if (days < 0) return `${Math.abs(days)} days overdue`;
  if (days === 0) return 'Due today';
  return `${days} days`;
};

const EMPTY_FORM = {
  SecretName: '', DisplayName: '', Environment: 'PreProd', SourceType: 'KeyVault',
  TrackingMode: 'Deadline', VaultName: '', ExpiresOnUtc: '', LastRotatedUtc: '',
  RotationIntervalDays: '', WarnDays: '90', OwnerId: '', IsActive: true, Notes: ''
};

const CredentialHealthPage = () => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { notify } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await credentialHealthApi.getAll();
      setRows(res.data || []);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to load the credential inventory');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // Owner picker. A failure here must not block the page — the inventory is still
  // readable and editable without an owner list.
  useEffect(() => {
    adminManagementApi.getUsers()
      .then(res => setUsers(res.data || []))
      .catch(() => setUsers([]));
  }, []);

  const formFromRow = (row) => ({
    SecretName:           row.secretName || '',
    DisplayName:          row.displayName || '',
    Environment:          row.environment || '',
    SourceType:           row.sourceType || '',
    TrackingMode:         row.trackingMode || '',
    VaultName:            row.vaultName || '',
    ExpiresOnUtc:         toDateInput(row.expiresOnUtc),
    LastRotatedUtc:       toDateInput(row.lastRotatedUtc),
    storedExpiresOnUtc:   row.expiresOnUtc || null,
    storedLastRotatedUtc: row.lastRotatedUtc || null,
    RotationIntervalDays: row.rotationIntervalDays === null || row.rotationIntervalDays === undefined ? '' : String(row.rotationIntervalDays),
    WarnDays:             row.warnDays === null || row.warnDays === undefined ? '90' : String(row.warnDays),
    OwnerId:              row.ownerId === null || row.ownerId === undefined ? '' : String(row.ownerId),
    IsActive:             row.isActive !== false,
    Notes:                row.notes || ''
  });

  const startAdd = () => {
    setSelected(null);
    setEditing({ ...EMPTY_FORM, isNew: true });
    setDirtyFields(new Set(['SecretName']));
  };

  const startEdit = () => {
    if (!selected) return;
    setEditing({ ...formFromRow(selected), isNew: false });
    setDirtyFields(new Set());
  };

  const cancelEdit = () => {
    if (dirtyFields.size > 0) {
      notify.confirm('Discard unsaved changes?', () => { setEditing(null); setDirtyFields(new Set()); });
      return;
    }
    setEditing(null);
    setDirtyFields(new Set());
  };

  const handleField = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
    const original = editing?.isNew ? EMPTY_FORM : formFromRow(selected || {});
    const next = new Set(dirtyFields);
    if (value !== original[field]) next.add(field); else next.delete(field);
    setDirtyFields(next);
  };

  const fieldDirty = (field) => dirtyFields.has(field);
  const inputClass = (field) => `stui-field-input${fieldDirty(field) ? ' dirty' : ''}`;

  const handleSave = async () => {
    if (!editing || dirtyFields.size === 0) return;
    if (!editing.SecretName?.trim()) {
      notify.error('Secret Name is required');
      return;
    }
    if (editing.SourceType === 'KeyVault' && !editing.VaultName?.trim()) {
      notify.error('Vault Name is required when Source Type is KeyVault — the refresh job matches on it');
      return;
    }

    const toInt = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));
    const payload = {
      SecretName:           editing.SecretName.trim(),
      DisplayName:          editing.DisplayName?.trim() || null,
      Environment:          editing.Environment,
      SourceType:           editing.SourceType,
      TrackingMode:         editing.TrackingMode,
      VaultName:            editing.VaultName?.trim() || null,
      ExpiresOnUtc:         editing.TrackingMode === 'Deadline' ? keepOrPick(editing.ExpiresOnUtc, editing.storedExpiresOnUtc) : null,
      LastRotatedUtc:       editing.TrackingMode === 'Rotation' ? keepOrPick(editing.LastRotatedUtc, editing.storedLastRotatedUtc) : null,
      RotationIntervalDays: editing.TrackingMode === 'Rotation' ? toInt(editing.RotationIntervalDays) : null,
      WarnDays:             toInt(editing.WarnDays) ?? 90,
      OwnerId:              toInt(editing.OwnerId),
      IsActive:             !!editing.IsActive,
      Notes:                editing.Notes?.trim() || null
    };

    setSaving(true);
    try {
      const saved = editing.isNew
        ? await credentialHealthApi.create(payload)
        : await credentialHealthApi.update(selected.id, payload);

      setEditing(null);
      setDirtyFields(new Set());
      const res = await credentialHealthApi.getAll();
      setRows(res.data || []);
      const refreshed = (res.data || []).find(r => r.id === (saved.data?.id ?? selected?.id));
      setSelected(refreshed || null);
      notify.success(`Saved ${payload.SecretName}`);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!selected) return;
    notify.confirm(`Stop tracking '${selected.secretName}' in ${selected.environment}? The credential itself is untouched — this only removes it from the inventory.`, async () => {
      try {
        await credentialHealthApi.remove(selected.id);
        setSelected(null);
        setEditing(null);
        setDirtyFields(new Set());
        await load();
        notify.success('Removed from inventory');
      } catch (err) {
        notify.error(err.response?.data?.error || 'Delete failed');
      }
    });
  };

  const isEditing = !!editing;
  const isNewRecord = !!editing?.isNew;
  const hasDirty = dirtyFields.size > 0;
  const needsAttention = rows.filter(r => r.status !== 'OK').length;

  const filtered = searchTerm.trim()
    ? rows.filter(r => {
        const q = searchTerm.trim().toLowerCase();
        return (r.secretName || '').toLowerCase().includes(q)
            || (r.displayName || '').toLowerCase().includes(q)
            || (r.environment || '').toLowerCase().includes(q)
            || (r.vaultName || '').toLowerCase().includes(q)
            || (r.status || '').toLowerCase().includes(q);
      })
    : rows;

  if (loading) return <div className="stui-detail-empty"><KeyRound size={48} /><p>Loading...</p></div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <KeyRound size={20} />
          <h1>Credential Health</h1>
          <span className="stui-module-header-meta">
            {rows.length} tracked · {needsAttention} needing attention
          </span>
        </div>
        <div className="stui-module-header-actions">
          {isEditing ? (
            <button
              className={`stui-crud-btn stui-delete-btn ${isNewRecord ? 'disabled' : ''}`}
              onClick={handleDelete}
              disabled={isNewRecord}
              title="Stop tracking this credential"
            >
              <Trash2 size={18} />
            </button>
          ) : (
            <button className="stui-crud-btn" onClick={startAdd} title="Track a new credential">
              <Plus size={18} />
            </button>
          )}
          <button
            className={`stui-crud-btn ${!isEditing && !selected ? 'disabled' : ''}`}
            onClick={isEditing ? cancelEdit : startEdit}
            disabled={!selected && !isEditing}
            title={isEditing ? 'Cancel' : 'Edit'}
          >
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button
            className={`stui-crud-btn ${!hasDirty ? 'disabled' : ''}`}
            onClick={handleSave}
            disabled={!hasDirty || saving}
            title="Save"
          >
            <Save size={18} />
          </button>
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions">
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-list-view">
        {listCollapsed ? (
          <div className="stui-list-panel stui-collapsed">
            <button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand">
              <ChevronRight size={16} />
            </button>
          </div>
        ) : (
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse">
                <ChevronLeft size={16} />
              </button>
              <div className="stui-search-wrapper">
                <Search size={14} className="stui-search-icon" />
                <input
                  className="stui-field-input"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search credentials..."
                />
              </div>
            </div>
            <div className="stui-list-scroll">
              {filtered.length === 0 ? (
                <div className="stui-list-empty">
                  {searchTerm ? 'No matches.' : 'Nothing tracked yet.'}
                </div>
              ) : (
                filtered.map(row => (
                  <button
                    key={row.id}
                    onClick={() => { setSelected(row); setEditing(null); setDirtyFields(new Set()); }}
                    className={`stui-list-item ${selected?.id === row.id ? 'selected' : ''}`}
                  >
                    <span className="stui-list-item-primary">{row.displayName || row.secretName}</span>
                    <span className="stui-list-item-secondary">
                      {row.environment} · {fmtDays(row.daysRemaining)}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        <div className="stui-detail-panel">
          {editing ? (
            <div className="stui-detail-content">
              <div className="stui-detail-grid">
                <div className="stui-col-24">
                  <div className="stui-detail-section-header">
                    {isNewRecord ? 'Track a New Credential' : `Edit — ${editing.SecretName}`}
                  </div>
                </div>

                <div className="stui-field-group stui-col-12">
                  <label>Secret Name</label>
                  <input
                    className={inputClass('SecretName')}
                    value={editing.SecretName}
                    onChange={e => handleField('SecretName', e.target.value)}
                    placeholder="e.g. Graph--ClientSecret"
                    autoFocus={isNewRecord}
                  />
                  <span className="stui-hint">
                    For a Key Vault row this must match the vault's secret name exactly — the refresh job matches on it.
                  </span>
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Display Name</label>
                  <input
                    className={inputClass('DisplayName')}
                    value={editing.DisplayName}
                    onChange={e => handleField('DisplayName', e.target.value)}
                    placeholder="Friendly label"
                  />
                </div>

                <div className="stui-field-group stui-col-8">
                  <label>Environment</label>
                  <LookupSelect
                    category="Secret_Environment"
                    value={editing.Environment}
                    onChange={v => handleField('Environment', v)}
                    className={inputClass('Environment')}
                    allowEmpty={false}
                  />
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Source Type</label>
                  <LookupSelect
                    category="Secret_SourceType"
                    value={editing.SourceType}
                    onChange={v => handleField('SourceType', v)}
                    className={inputClass('SourceType')}
                    allowEmpty={false}
                  />
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Tracking Mode</label>
                  <LookupSelect
                    category="Secret_TrackingMode"
                    value={editing.TrackingMode}
                    onChange={v => handleField('TrackingMode', v)}
                    className={inputClass('TrackingMode')}
                    allowEmpty={false}
                  />
                  <span className="stui-hint">
                    Deadline = dies on a date. Rotation = never expires, so age is the risk.
                  </span>
                </div>

                <div className="stui-field-group stui-col-12">
                  <label>Vault Name</label>
                  <input
                    className={inputClass('VaultName')}
                    value={editing.VaultName}
                    onChange={e => handleField('VaultName', e.target.value)}
                    placeholder="pmd-preprod-keyvault"
                    disabled={editing.SourceType !== 'KeyVault'}
                  />
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Owner</label>
                  <StuiSelect
                    className={inputClass('OwnerId')}
                    value={editing.OwnerId}
                    onChange={e => handleField('OwnerId', e.target.value)}
                  >
                    <option value="">-- Unassigned --</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.displayName || u.email}</option>
                    ))}
                  </StuiSelect>
                </div>

                {editing.TrackingMode === 'Rotation' ? (
                  <>
                    <div className="stui-field-group stui-col-8">
                      <label>Last Rotated</label>
                      <StuiDateTime
                        mode="date"
                        valueZone="none"
                        value={editing.LastRotatedUtc}
                        dirty={fieldDirty('LastRotatedUtc')}
                        onChange={v => handleField('LastRotatedUtc', v)}
                      />
                    </div>
                    <div className="stui-field-group stui-col-8">
                      <label>Rotation Interval (days)</label>
                      <input
                        type="number"
                        min="1"
                        className={inputClass('RotationIntervalDays')}
                        value={editing.RotationIntervalDays}
                        onChange={e => handleField('RotationIntervalDays', e.target.value)}
                        placeholder="365"
                      />
                    </div>
                  </>
                ) : (
                  <div className="stui-field-group stui-col-16">
                    <label>Expires On</label>
                    <StuiDateTime
                      mode="date"
                      valueZone="none"
                      value={editing.ExpiresOnUtc}
                      dirty={fieldDirty('ExpiresOnUtc')}
                      onChange={v => handleField('ExpiresOnUtc', v)}
                    />
                    <span className="stui-hint">
                      Leave blank if genuinely unknown — the row then reads Unknown, which is more use than a guess.
                    </span>
                  </div>
                )}

                <div className="stui-field-group stui-col-4">
                  <label>Warn Days</label>
                  <input
                    type="number"
                    min="1"
                    className={inputClass('WarnDays')}
                    value={editing.WarnDays}
                    onChange={e => handleField('WarnDays', e.target.value)}
                  />
                </div>
                <div className="stui-field-group stui-col-4">
                  <label>Active</label>
                  <label className="stui-checkbox">
                    <input
                      type="checkbox"
                      className="stui-checkbox-input"
                      checked={!!editing.IsActive}
                      onChange={e => handleField('IsActive', e.target.checked)}
                    />
                  </label>
                </div>

                <div className="stui-field-group stui-col-24">
                  <label>Notes</label>
                  <input
                    className={inputClass('Notes')}
                    value={editing.Notes}
                    onChange={e => handleField('Notes', e.target.value)}
                    placeholder="What breaks if this lapses, and who to talk to"
                  />
                </div>
              </div>
            </div>
          ) : selected ? (
            <div className="stui-detail-content">
              <div className="stui-detail-grid">
                <div className="stui-col-24">
                  <div className="stui-detail-section-header">
                    {selected.displayName || selected.secretName}
                  </div>
                </div>

                <div className="stui-field-group stui-col-8">
                  <label>Status</label>
                  <div className="stui-field-display">
                    <span className={getStatusClass(selected.status)}>{selected.status}</span>
                  </div>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Action Due</label>
                  <div className="stui-field-display">
                    {fmtDate(selected.actionDueUtc)} · {fmtDays(selected.daysRemaining)}
                  </div>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Tracking Mode</label>
                  <div className="stui-field-display">
                    <span className={getModeClass(selected.trackingMode)}>{selected.trackingMode}</span>
                  </div>
                </div>

                <div className="stui-field-group stui-col-12">
                  <label>Secret Name</label>
                  <div className="stui-field-display stui-mono">{selected.secretName}</div>
                </div>
                <div className="stui-field-group stui-col-6">
                  <label>Environment</label>
                  <div className="stui-field-display">{selected.environment}</div>
                </div>
                <div className="stui-field-group stui-col-6">
                  <label>Source</label>
                  <div className="stui-field-display">{selected.sourceType}</div>
                </div>

                <div className="stui-field-group stui-col-12">
                  <label>Vault</label>
                  <div className="stui-field-display">{selected.vaultName || '—'}</div>
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Owner</label>
                  <div className="stui-field-display">{selected.ownerName || 'Unassigned'}</div>
                </div>

                {selected.trackingMode === 'Rotation' ? (
                  <>
                    <div className="stui-field-group stui-col-8">
                      <label>Last Rotated</label>
                      <div className="stui-field-display">{fmtDate(selected.lastRotatedUtc)}</div>
                    </div>
                    <div className="stui-field-group stui-col-8">
                      <label>Rotation Interval</label>
                      <div className="stui-field-display">
                        {selected.rotationIntervalDays ? `${selected.rotationIntervalDays} days` : '—'}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="stui-field-group stui-col-16">
                    <label>Expires On</label>
                    <div className="stui-field-display">{fmtDate(selected.expiresOnUtc)}</div>
                  </div>
                )}

                <div className="stui-field-group stui-col-8">
                  <label>Warn Days</label>
                  <div className="stui-field-display">{selected.warnDays}</div>
                </div>

                <div className="stui-field-group stui-col-24">
                  <label>Last Confirmed From Source</label>
                  <div className="stui-field-display">
                    {selected.autoRefreshedUtc
                      ? fmtDate(selected.autoRefreshedUtc)
                      : selected.sourceType === 'KeyVault'
                        ? 'Never — these dates are typed, not confirmed by the vault'
                        : 'Not applicable — this row is maintained by hand'}
                  </div>
                </div>

                <div className="stui-field-group stui-col-24">
                  <label>Notes</label>
                  <div className="stui-field-display">{selected.notes || '—'}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="stui-detail-empty">
              <KeyRound size={48} />
              <p>Select a credential to see when it next needs attention, or click + to track a new one.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CredentialHealthPage;
